import { ContextVersionId, OrgGuideFilePath } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { MonacoEditorOptions } from "../../components/cell/monaco/options";
import {
  ContextWorkbenchEditorSidebarPanel,
  ContextWorkbenchEditorTab,
  ContextWorkbenchFileId,
  ContextWorkbenchHistoryTimelineSelectableNodeData,
  ContextWorkbenchState,
  getContextWorkbenchInitialState,
} from "../../components/context-workbench/redux";
import { ContextWorkbenchAskThread } from "../../components/context-workbench/redux/contextWorkbenchAskThread";
import { ContextWorkbenchHistory } from "../../components/context-workbench/redux/contextWorkbenchHistory";
import { ContextWorkbenchPublish } from "../../components/context-workbench/redux/contextWorkbenchPublish";
import {
  InitMagicRulesEntityPayload,
  initMagicRulesEntity,
} from "../../components/context-workbench/redux/reducers";
import {
  InitPublishBaseFilesPayload,
  InitPublishChangesetPayload,
  initPublishBaseFiles as initPublishBaseFilesImpl,
  initPublishChangeset as initPublishChangesetImpl,
} from "../../components/context-workbench/redux/reducers/publishChangesetReducers";
import { ContextWorkbenchSelectorOptions } from "../../components/context-workbench/redux/selectors/options";
import { contextWorkbenchPublishSelectors } from "../../components/context-workbench/redux/selectors/publishSelectors";
import { editorTabMatchesFile } from "../../components/context-workbench/redux/utils/editorTab";
import {
  ResizeWorkbenchRegionPayload,
  ToggleWorkbenchRegionPayload,
  resizeWorkbenchRegion,
  toggleWorkbenchRegion,
} from "../../components/workbench/redux-reducers/regionReducers";
import {
  CloseWorkbenchTabPayload,
  FocusWorkbenchTabPayload,
  NewWorkbenchTabPayload,
  UpdateWorkbenchTabPayload,
  closeWorkbenchTab,
  focusWorkbenchTab,
  newWorkbenchTab,
  openInWorkbenchTab,
  updateWorkbenchTab,
} from "../../components/workbench/redux-reducers/tabReducers";
import {
  UpdateWorkbenchViewportPayload,
  updateWorkbenchViewport,
} from "../../components/workbench/redux-reducers/viewportReducers";
import { filterTabs } from "../../components/workbench/state";
import { updateWorkbenchHistoryVersionPanelFileExpanded } from "../../components/workbench/state/history";
import { ContextWorkbenchQueryParams } from "../../route/routes";
import { RootState } from "../store";

const contextWorkbenchSlice = createSlice({
  name: "contextWorkbench",
  initialState: getContextWorkbenchInitialState(),
  reducers: {
    unmount(state: ContextWorkbenchState) {
      state.entities = getContextWorkbenchInitialState().entities;
    },
    // --- Editor tabs ---
    newEditorTab(
      state: ContextWorkbenchState,
      action: PayloadAction<NewWorkbenchTabPayload<ContextWorkbenchEditorTab>>,
    ) {
      newWorkbenchTab(state.editor, action.payload);
    },
    focusEditorTab(
      state: ContextWorkbenchState,
      action: PayloadAction<FocusWorkbenchTabPayload>,
    ) {
      focusWorkbenchTab(state.editor, action.payload);
    },
    closeEditorTab(
      state: ContextWorkbenchState,
      action: PayloadAction<CloseWorkbenchTabPayload>,
    ) {
      closeWorkbenchTab(state.editor, action.payload);
    },
    updateEditorTab(
      state: ContextWorkbenchState,
      action: PayloadAction<
        UpdateWorkbenchTabPayload<ContextWorkbenchEditorTab>
      >,
    ) {
      updateWorkbenchTab(state.editor, action.payload);
    },

    // --- Editor viewport ---
    updateEditorViewport(
      state: ContextWorkbenchState,
      action: PayloadAction<UpdateWorkbenchViewportPayload>,
    ) {
      updateWorkbenchViewport(state.editor, action.payload);
    },

    // --- Editor regions ---
    resizeEditorRegion(
      state: ContextWorkbenchState,
      action: PayloadAction<ResizeWorkbenchRegionPayload>,
    ) {
      resizeWorkbenchRegion(state.editor.regions, action.payload);
    },
    toggleEditorRegion(
      state: ContextWorkbenchState,
      action: PayloadAction<ToggleWorkbenchRegionPayload>,
    ) {
      toggleWorkbenchRegion(state.editor.regions, action.payload);
    },

    // --- Editor sidebar ---
    setEditorSidebarPanel(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        newPanelId: ContextWorkbenchEditorSidebarPanel.Id | null;
      }>,
    ) {
      state.editor.regions.left.isOpen = action.payload.newPanelId != null;
      state.editor.sidebar.panelId = action.payload.newPanelId;
    },

    // --- Editor files ---
    openEditorFileInTab(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        fileId: ContextWorkbenchFileId;
        options?: {
          readOnly: boolean;
          tabType?: ContextWorkbenchEditorTab.Type;
          isPreview?: boolean;
        };
      }>,
    ) {
      const { isPreview, readOnly, tabType } = action.payload.options ?? {};
      const inferredTabType = readOnly
        ? ContextWorkbenchEditorTab.Type.MarkdownPreview
        : ContextWorkbenchEditorTab.Type.FileEditor;

      openInWorkbenchTab<
        ContextWorkbenchState["editor"],
        ContextWorkbenchEditorTab
      >(
        state.editor,
        {
          fileId: action.payload.fileId,
          type: tabType ?? inferredTabType,
        },
        {
          getNewTabProperties: (payload) => ({
            ...payload,
            isPreview,
          }),
        },
      );
    },

    closeEditorFileTabs(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        fileId: ContextWorkbenchFileId;
      }>,
    ) {
      const tabs = filterTabs(
        state.editor.tabGroups,
        editorTabMatchesFile(action.payload.fileId),
      );
      for (const tab of tabs) {
        closeWorkbenchTab(state.editor, { tabId: tab.id });
      }
    },

    swapEditorFileTabs(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        fileIdBefore: ContextWorkbenchFileId;
        fileIdAfter: ContextWorkbenchFileId;
      }>,
    ) {
      const tabs = filterTabs(
        state.editor.tabGroups,
        editorTabMatchesFile(action.payload.fileIdBefore),
      );
      for (const tab of tabs) {
        updateWorkbenchTab<
          ContextWorkbenchState["editor"],
          ContextWorkbenchEditorTab
        >(state.editor, {
          tabId: tab.id,
          producerFn: (draftTab) => {
            draftTab.fileId = ContextWorkbenchFileId.check(
              action.payload.fileIdAfter,
            );
          },
        });
      }
    },

    // --- Entities ---
    initMagicRulesEntity(
      state: ContextWorkbenchState,
      action: PayloadAction<InitMagicRulesEntityPayload>,
    ) {
      initMagicRulesEntity(state, action.payload);
    },

    // --- Publish ---
    initPublishChangeset(
      state: ContextWorkbenchState,
      action: PayloadAction<InitPublishChangesetPayload>,
    ) {
      initPublishChangesetImpl(state, action.payload);
    },
    initPublishBaseFiles(
      state: ContextWorkbenchState,
      action: PayloadAction<InitPublishBaseFilesPayload>,
    ) {
      initPublishBaseFilesImpl(state, action.payload);
    },

    setPublishSelectedFileId(
      state: ContextWorkbenchState,
      action: PayloadAction<ContextWorkbenchFileId | null>,
    ) {
      state.publish.selectedFileId = action.payload;
    },
    setPublishChangesFilter(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        value: ContextWorkbenchPublish.ChangesFilter;
        options: ContextWorkbenchSelectorOptions;
      }>,
    ) {
      state.publish.changesFilter = action.payload.value;
      const selectedFileId = state.publish.selectedFileId;
      if (!selectedFileId) {
        state.publish.selectedFileId =
          contextWorkbenchPublishSelectors.selectFirstVisibleFile(
            { contextWorkbench: state },
            action.payload.options,
          );
      } else if (
        action.payload.value ===
        ContextWorkbenchHistory.VersionPanelChangesFilter.CHANGED
      ) {
        const visibleFiles =
          contextWorkbenchPublishSelectors.selectVisibleFiles(
            { contextWorkbench: state },
            action.payload.options,
          );
        if (!visibleFiles.find((f) => f.id === selectedFileId)) {
          state.publish.selectedFileId =
            contextWorkbenchPublishSelectors.selectFirstVisibleFile(
              { contextWorkbench: state },
              action.payload.options,
            );
        }
      }
    },

    setPublishAskPreviewThread(
      state: ContextWorkbenchState,
      action: PayloadAction<ContextWorkbenchAskThread | null>,
    ) {
      state.publish.askPreviewThread = action.payload;
    },

    resetPublishState(state: ContextWorkbenchState) {
      state.publish.askPreviewThread = null;
      state.publish.selectedFileId = null;
      state.publish.changesFilter =
        ContextWorkbenchPublish.ChangesFilter.CHANGED;
    },

    syncPublishStateFromRouteQueryParams(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        queryParams: {
          publishView: ContextWorkbenchQueryParams.PublishView | null;
          publishChangesetId: ContextWorkbenchQueryParams.PublishChangesetId | null;
        };
      }>,
    ) {
      const { publishChangesetId, publishView } = action.payload.queryParams;
      if (publishView == null) {
        state.view = "editor";
      } else {
        state.view = "publish";
        state.publish.view = publishView;
      }
      state.publish.changeset.loadId = publishChangesetId ?? undefined;
    },

    toggleEditorSidebarHistoryTimelineSortDirection(
      state: ContextWorkbenchState,
      _action: PayloadAction<void>,
    ) {
      const nextSortDirection =
        state.editor.sidebar.history.timelineSortDirection === "ASC"
          ? "DESC"
          : "ASC";
      state.editor.sidebar.history.timelineSortDirection = nextSortDirection;
    },
    toggleHistoryTimelineSortDirection(
      state: ContextWorkbenchState,
      _action: PayloadAction<void>,
    ) {
      const nextSortDirection =
        state.history.timeline.sortDirection === "ASC" ? "DESC" : "ASC";
      state.history.timeline.sortDirection = nextSortDirection;
    },
    setHistoryTimelineSelectedNodeId(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        timelineNodeId: ContextWorkbenchHistoryTimelineSelectableNodeData["id"];
      }>,
    ) {
      state.history.timeline.selectedNodeId = action.payload.timelineNodeId;
    },
    openHistoryDialog(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        timelineNodeId:
          | ContextWorkbenchHistoryTimelineSelectableNodeData["id"]
          | null;
      }>,
    ) {
      state.history.dialog.isOpen = true;
      state.history.timeline.selectedNodeId = action.payload.timelineNodeId;
    },
    closeHistoryDialog(state: ContextWorkbenchState) {
      state.history.dialog.isOpen = false;
      state.history.timeline.selectedNodeId = null;
    },
    setHistoryVersionPanelTabId(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        newVersionPanelTabId: ContextWorkbenchHistory.VersionPanelTabId;
      }>,
    ) {
      state.history.versionPanel.tabId = action.payload.newVersionPanelTabId;
    },
    setHistoryVersionPanelFileExpanded(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        fileGroupId: ContextVersionId;
        filePath: OrgGuideFilePath;
        expanded: boolean;
      }>,
    ) {
      state.history.versionPanel.fileExpanded =
        updateWorkbenchHistoryVersionPanelFileExpanded(
          state.history.versionPanel.fileExpanded,
          {
            fileGroupId: action.payload.fileGroupId,
            filePath: action.payload.filePath,
            expanded: action.payload.expanded,
          },
        );
    },
    setHistoryVersionPanelChangesFilter(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        value: ContextWorkbenchHistory.VersionPanelChangesFilter;
      }>,
    ) {
      state.history.versionPanel.changesFilter = action.payload.value;
    },
    setHistoryEditorOptions(
      state: ContextWorkbenchState,
      action: PayloadAction<{
        options: Partial<MonacoEditorOptions>;
      }>,
    ) {
      state.history.editor.options = {
        ...state.history.editor.options,
        ...action.payload.options,
      };
    },
    setPublishFormData(
      state: ContextWorkbenchState,
      action: PayloadAction<Partial<ContextWorkbenchPublish.FormData>>,
    ) {
      if (typeof action.payload.title === "string") {
        state.publish.formData.title = action.payload.title;
      }
      if (typeof action.payload.description === "string") {
        state.publish.formData.description = action.payload.description;
      }
    },
    resetPublishFormData(state: ContextWorkbenchState) {
      state.publish.formData = {
        title: "",
        description: "",
      };
    },
  },
});

export const contextWorkbenchReducer = contextWorkbenchSlice.reducer;

export const contextWorkbenchActions = contextWorkbenchSlice.actions;

// *** Selectors **************************************************************
export function selectContextWorkbench(
  state: Pick<RootState, "contextWorkbench">,
): ContextWorkbenchState {
  return state.contextWorkbench;
}
